import Actions from "../../Actions";
import DashboardActions from "./DashboardActions";
import { calculateExtraLocationBudget, listOfStatus } from "../../utils/Helpers";
import { ALL_STATUSES } from "./DashboardStore";

import _ from "lodash";
import { getExtraLocationCosts } from "../SelfServiceCampaignCreation/campaign-creation/booking-overview/utils/utils";

/**
 * Filters campaigns that were created in the last month.
 * @param {Object[]} campaigns - List of campaign objects.
 * @returns {Object[]} Filtered array of campaign objects created in the last month.
 */
export function getLastMonthCampaigns(campaigns) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const lastMonthStart = new Date(currentYear, currentMonth - 1, 1);
  const lastMonthEnd = new Date(currentYear, currentMonth, 1);

  return campaigns.filter((campaign) => {
    const createdDate = new Date(campaign.createdDate);
    return createdDate >= lastMonthStart && createdDate < lastMonthEnd;
  });
}

/**
 * Filters campaigns that were created in the last six months.
 * @param {Object[]} campaigns - List of campaign objects.
 * @returns {Object[]} Filtered array of campaign objects created in the last six months.
 */
export function getLastSixMonthCampaigns(campaigns) {
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);

  return campaigns.filter((campaign) => {
    const createdDate = new Date(campaign.createdDate);
    return createdDate >= sixMonthsAgo && createdDate < now;
  });
}

/**
 * Filters campaigns based on the provided years.
 * @param {Object[]} campaigns - List of campaign objects.
 * @param {number[]} yearsFilter - Array of years to filter the campaigns by.
 * @returns {Object[]} Filtered array of campaign objects. Returns all campaigns if yearsFilter is empty.
 */
export function getYearCampaigns(campaigns, yearsFilter) {
  if (yearsFilter.length === 0) {
    return campaigns;
  }

  return campaigns.filter((campaign) => {
    const createdDate = new Date(campaign.createdDate);
    const campaignYear = createdDate.getFullYear();
    return yearsFilter.includes(campaignYear);
  });
}

/**
 * Function to get the name of the previous month.
 * @returns {string} The name of the previous month.
 */
export function getLastMonthName() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  const month = date.toLocaleString("de", { month: "long" });
  return month;
}

/**
 * Fetch campaigns based on specified statuses and organization ID.
 * @param {Object} options - The options object.
 * @param {string[]} options.statuses - Array of statuses to filter campaigns by.
 * @param {string} options.organizationId - ID of the organization to fetch campaigns for.
 * @param {boolean} options.finishedCampaignsHaveBeenLoaded - Flag indicating if finished campaigns have been loaded.
 */
export const fetchCampaigns = ({ statuses, organizationId, finishedCampaignsHaveBeenLoaded }) => {
  // All campaigns except finished are loaded from first rendered.
  // Now that we are refetching we could re-load all including finished if it'ts required
  if (statuses.includes("FINISHED") && !finishedCampaignsHaveBeenLoaded) {
    Actions.loadFilteredCampaigns({
      organizationId: organizationId,
      filters: { statuses: ALL_STATUSES }
    });
    DashboardActions.setFinishedCampaignsHaveBeenLoaded(true);
  }
};

/**
 * Debounced version of fetchCampaigns function.
 * @param {Object} options - The options object.
 * @param {string[]} options.statuses - Array of statuses to filter campaigns by.
 * @param {string} options.organizationId - ID of the organization to fetch campaigns for.
 * @param {boolean} options.finishedCampaignsHaveBeenLoaded - Flag indicating if finished campaigns have been loaded.
 */
export const debouncedFetchCampaigns = _.debounce(
  ({ statuses, organizationId, finishedCampaignsHaveBeenLoaded }) => {
    fetchCampaigns({ statuses, organizationId, finishedCampaignsHaveBeenLoaded });
  },
  1500
);

/**
 * Extracts values from a list of campaign objects based on a specified key.
 * @param {Object[]} campaignList - List of campaign objects.
 * @param {string} key - Key of the property to extract from each campaign object.
 * @returns {string[]} Array of unique values extracted from the specified key in the campaign objects.
 */
export const getNamesFromCampaignList = (campaignList, key) => {
  const list = new Set();
  campaignList.forEach((campaign) => list.add(campaign[key]));
  const listAsArray = Array.from(list).sort((a, b) => a.localeCompare(b));
  return listAsArray;
};

/**
 * Array of status options objects.
 * @type {Object[]}
 */
export const statusOptions = (t) =>
  listOfStatus(t).map((status) => ({
    value: status.value,
    label: status.name,
    color: status.alternativeColor
  }));

/**
 * Creates options objects from an array of string names.
 * @param {string[]} names - Array of string names.
 * @returns {Object[]} Array of options objects with 'value' and 'label' properties.
 */
export const createOptionsFromStringArray = (names) => {
  return names
    .map((name) => {
      return { value: name, label: name };
    })
    .filter((option) => option.value && option.value !== "")
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Filters campaigns based on a specified property and selection criteria.
 * @param {Object} options - The options object.
 * @param {Object[]} options.campaigns - List of campaign objects to filter.
 * @param {string} options.on - Property of the campaign objects to filter by.
 * @param {string[]} options.selection - Array of values to filter the campaigns on the specified property.
 * @returns {Object[]} Filtered array of campaign objects.
 */
export const filterCampaignsBasedOn = ({ campaigns, on, selection }) =>
  campaigns.filter((campaign) => selection.includes(campaign[on]));

/**
 * Get the campaign budget cost from the addition of budget and add-ons.
 *
 * @param {Object} props - The props object containing budget details.
 * @param {number} props.combinedBudget - The calculated full budget allocated for the campaign.
 * @param {Array<{ label: string, type: string }>} props.locations - An array of location objects, where each location has a `label` (name) and a `type` (`"state"` or other values).
 * @param {number} props.lifeTimeBudget - The base budget allocated for the campaign.
 * @param {number} [props.feedbackAnalysis=0] - The cost associated with feedback analysis (default is 0 if not provided).
 * @param {number} [props.optimizeAnalysis=0] - The cost associated with optimization analysis (default is 0 if not provided).
 * @param {number} [props.spotifyHumanVoice=0] - The cost for using human voice in Spotify ads (default is 0 if not provided).
 * @returns {number} The total calculated campaign cost.
 */
export const getCostBreakdown = ({
  combinedBudget,
  locations,
  lifeTimeBudget,
  feedbackAnalysis,
  optimizeAnalysis,
  spotifyHumanVoice,
  lastEditStage
}) => {
  if (combinedBudget) return Number(combinedBudget) || 0;

  const extraBudgetLocations = calculateExtraLocationBudget(locations);
  const extraLocationsCost = getExtraLocationCosts(extraBudgetLocations);
  const applyAddons = ["STEP_REVIEW", "STEP_FINISHED", "STEP_FINISHED_NEW"].includes(lastEditStage);

  const baseCost = Number(lifeTimeBudget) || 0;
  const feedbackCost = Number(feedbackAnalysis) || 0;
  const optimizeCost = Number(optimizeAnalysis) || 0;
  const spotifyCost = Number(spotifyHumanVoice) || 0;

  let campaignCost = baseCost + extraLocationsCost;
  if (applyAddons) {
    campaignCost = campaignCost + feedbackCost + optimizeCost + spotifyCost;
  }
  return isNaN(campaignCost) ? 0 : campaignCost;
};
